import { Box, useTheme } from "@mui/material";
import { tokens } from "../../../../../theme";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import ShCopyButton from '../../../../../components/theme/ShCopy';
import SidepanelLoader from '../../../../../components/theme/sidepanel2/SidepanelLoader';
import ShLabelSkeleton from "../../../../../components/theme/skeleton/ShLabelSkeleton";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import { useGetOrdersQuery } from "../../../../../newapi/order/ordersSlice";
import { getOrderStateData } from "../../../../../utils/labelColorText";
import { general_states } from '../../../../../utils/staticEnums';
import { ORDER_FILTER_KEYS } from '../../../../../components/global/DataGrid/Filter';
import Text from "../../../../../components/theme/text/Text";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import { SwapHorizOutlined } from '@mui/icons-material';

const SpOrderDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    
    return (
        <ButtonGroup version={2}>
            <Shbutton 
                className={"group-btn"} 
                color={"secondary"} 
                onClick={() => props.toggleShowAll()}
                endIcon={<SwapHorizOutlined/>}
                variant="contained"
            >
                {props.showAll ? t('toggle_show_open') : t('toggle_show_all')}
            </Shbutton>
        </ButtonGroup>
    );
};

let gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [ORDER_FILTER_KEYS.STATE],
};

const WebshopCredentialsOrders = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState([]);
    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();
    const [config, setConfig] = useState({
        webshopCredentialsId: props.webshopCredentialsId,
        [ORDER_FILTER_KEYS.STATE.backend_key]: [
            general_states.OPEN,
            general_states.PICKING_IN_PROGRESS,
            general_states.FULLYPICKED,
            general_states.ECHECK,
            general_states.ECHECKPAUSED
        ],
        stateAll: false
    });
    const [showAll, setShowAll] = useState(false);

    const ref = useRef();

    const toggleShowAll = () => {
        setShowAll(!showAll);
    };

    // Update config when webshopCredentialsId prop changes
    useEffect(() => {
        setConfig(prevConfig => ({
            ...prevConfig,
            webshopCredentialsId: props.webshopCredentialsId
        }));
        // Clear data when switching webshop credentials to prevent old data from showing
        setData([]);
    }, [props.webshopCredentialsId]);

    useEffect(() => {
        if(config.stateAll !== showAll) {
            setConfig(prevConfig => ({
                ...prevConfig,
                stateAll: showAll,
                [ORDER_FILTER_KEYS.STATE.backend_key]: showAll ? [] : [
                    general_states.OPEN,
                    general_states.PICKING_IN_PROGRESS,
                    general_states.FULLYPICKED,
                    general_states.ECHECK,
                    general_states.ECHECKPAUSED
                ]
            }));
        }
    }, [showAll]);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
        });

        return () => {
            
        }
    }, []);

    const [orderId, setOrderId] = useState();
    const orderIdRef = useRef(orderId);
    
    const getOrderDetails = (params) => {
        if (!params) return;
    
        const neworderId = parseInt(params.row.id);
        // Only update state if the orderId has actually changed
        if (orderIdRef.current !== neworderId) {
            if (!spLoading) {
                setSpLoading(true);
            }
            setOrderId(neworderId);
            orderIdRef.current = neworderId; // Update the ref to the new value
        }
        setIsExtended(true);
    };

    const columns = [
        {
            field: "barcode",
            headerName: t('order'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                    <Box sx={{
                        minWidth: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        whiteSpace: "nowrap",
                        wordBreak: "keep-all",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        ".copyel": {
                            opacity: 0,
                        },
                        "&:hover .copyel": {
                            opacity: 1,
                        }
                    }}>
                        <Text noBreak>{params.row.barcode}</Text>
                        <ShCopyButton 
                            styles={{button: {
                                background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[200],
                                color: colors.txt["primary"],
                                "&:hover": {
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[300],
                                }
                            }}} 
                            sx={{position: "absolute", right: 0, zIndex: 3000}} 
                            className={"copyel"} 
                            closeAfter={1500} 
                            value={params.row.barcode}
                        ></ShCopyButton>
                    </Box>
                );
            }
        },
        {
            field: "deliveryName", 
            headerName: t("name"), 
            flex: 1, 
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.deliveryName
            }
        },
        {
            field: "webshopreference", 
            headerName: t("webshopreference"), 
            flex: 2,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.webshopreference
            }
        },
        {
            field: "state", 
            headerName: t("state"), 
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }
                
                const stateData = getOrderStateData(params.row.state, theme);
                return (
                    <ShLabel variant={stateData.labelcolor}  palette="normal">
                        {t(stateData.labeltext)}
                    </ShLabel>
                );
            }
        },
        {
            field: "createdAt", 
            headerName: t("created"), 
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                
                const date = new Date(params.row.createdAt);
                const dateStr = date.toLocaleDateString(i18n.language, { dateStyle: 'short' });
                const timeStr = date.toLocaleTimeString(i18n.language, { timeStyle: 'short' });

                return (
                    <span>
                        {dateStr}{' '}<Text light>{timeStr}</Text>
                    </span>
                );
            }
        },
        {
            field: "phoneView", 
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <Box>
                        {/* Mobile card view would go here */}
                    </Box>
                )
            }
        }
    ];

    return (
        <Box height={1} ref={ref} display={"flex"} flexDirection={"column"}>
            <ShDataGrid2 
                key={`${theme.palette.mode}-${config.webshopCredentialsId}-${config.stateAll}`}
                content={useGetOrdersQuery}
                config={config}
                title={t('orders')}
                gridOptions={gridOptions} 
                gridActions={<GridActions toggleShowAll={toggleShowAll} showAll={showAll} />}
                columns={columns}
                rows={data}
                sortModel={{field: columns[0].field, type: "DESC"}}
                onRowClick={getOrderDetails}
            >
            </ShDataGrid2>

            {spLoading && 
                <SpOrderDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    orderId={orderId}
                /> 
            }
        </Box>
    );
}

export default WebshopCredentialsOrders; 